import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { OrbisMarkSVG } from '@/components/OrbisLogo';
import ReviewStage from '@/components/magic/ReviewStage';
import {
  ArrowLeft, ArrowRight, Check, Sparkles, Eye,
  Upload, Monitor, Tablet, Smartphone, Loader2,
  ChevronDown, ChevronUp, FileText, CheckSquare, Square, Layers,
  Wand2, RotateCcw, Globe,
} from 'lucide-react';
import { getTemplateById, TEMPLATES } from '@/data/templates';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GOOGLE_FONTS = [
  'Inter', 'Space Grotesk', 'Sora', 'Syne', 'DM Sans', 'Plus Jakarta Sans',
  'Outfit', 'Manrope', 'Poppins', 'Montserrat', 'Playfair Display',
  'Cormorant Garamond', 'Orbitron', 'JetBrains Mono', 'Nunito', 'Raleway',
  'Work Sans', 'Figtree', 'General Sans', 'Satoshi',
];

const PRE_RENDER_STEPS = ['Brand', 'Pages', 'Content'];

export default function TemplateEditorPage() {
  const { templateId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [step, setStep] = useState(0);
  const [template, setTemplate] = useState(null);
  const [projectId, setProjectId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [rendering, setRendering] = useState(false);

  // After render — ReviewStage takes over
  const [magicState, setMagicState] = useState(null);

  // Brand
  const [brandKit, setBrandKit] = useState({
    logoUrl: null,
    colors: { primary: '#7c3aed', secondary: '#06b6d4', accent: '#f59e0b', background: '#09090b', text: '#fafafa' },
    fonts: { heading: 'Inter', body: 'Inter' },
  });

  // Pages
  const [vaultPages, setVaultPages] = useState(null); // { pages, grouped, total_pages }
  const [selectedPages, setSelectedPages] = useState(new Set());

  // Content
  const [contentOverrides, setContentOverrides] = useState({});
  const [imageOverrides, setImageOverrides] = useState({});

  // Preview (pre-render only)
  const [previewHtml, setPreviewHtml] = useState('');
  const [viewport, setViewport] = useState('desktop');
  const [currentPageIdx, setCurrentPageIdx] = useState(0);

  // ── Init ──
  useEffect(() => { init(); }, [templateId]);

  const init = async () => {
    setLoading(true);
    try {
      const existingTemplate = getTemplateById(templateId);
      if (existingTemplate) {
        // Create new project from template
        setTemplate(existingTemplate);
        setBrandKit(existingTemplate.defaultBrandKit);
        try {
          const resp = await axios.post(`${API_URL}/templates/projects`, {
            name: `${existingTemplate.name} Project`,
            template_id: existingTemplate.id,
            template_slug: existingTemplate.slug,
            brand_context: {
              brand_path: 'full',
              colors: existingTemplate.defaultBrandKit.colors,
              heading_font: existingTemplate.defaultBrandKit.fonts.heading,
              body_font: existingTemplate.defaultBrandKit.fonts.body,
            },
          });
          setProjectId(resp.data.id);
        } catch (e) {
          console.warn('API unavailable, running in local preview mode');
        }
      } else {
        // Fetch existing project
        const resp = await axios.get(`${API_URL}/templates/projects/${templateId}`);
        setProjectId(resp.data.id);
        const state = resp.data.template_state || {};
        const slug = state.template_slug;
        const tmpl = TEMPLATES.find(t => t.slug === slug) || TEMPLATES[0];
        setTemplate(tmpl);
        if (state.brand_context) {
          setBrandKit({
            logoUrl: state.brand_context.logo_url || null,
            colors: state.brand_context.colors || tmpl.defaultBrandKit.colors,
            fonts: {
              heading: state.brand_context.heading_font || tmpl.defaultBrandKit.fonts.heading,
              body: state.brand_context.body_font || tmpl.defaultBrandKit.fonts.body,
            },
          });
        }
        if (state.content_overrides) setContentOverrides(state.content_overrides);
        if (state.image_overrides) setImageOverrides(state.image_overrides);

        // If already rendered, go straight to ReviewStage
        if (state.rendered && state.generated_code) {
          setMagicState(state);
        }
      }
    } catch (err) {
      toast.error('Failed to load template');
      navigate('/templates');
    } finally {
      setLoading(false);
    }
  };

  // ── Preview builder (pre-render only) ──
  const buildPreview = useCallback(async () => {
    if (!template || magicState) return;
    const page = template.pages[currentPageIdx];
    if (!page) return;
    try {
      const resp = await fetch(page.htmlPath);
      let html = await resp.text();

      let templateDir = page.htmlPath.substring(0, page.htmlPath.lastIndexOf('/') + 1);
      let baseTag = '';
      if (page.htmlPath.startsWith('http')) {
        baseTag = `<base href="${templateDir}">`;
      } else {
        if (templateDir.startsWith('/')) templateDir = templateDir.substring(1);
        baseTag = `<base href="${window.location.origin}/${templateDir}">`;
      }
      const interceptScript = `<script>document.addEventListener('click', function(e) { const a = e.target.closest('a'); if(a) { e.preventDefault(); }});</script>`;
      html = html.replace(/<head(\s[^>]*)?>/i, `<head$1>\n  ${baseTag}\n  ${interceptScript}`);

      // Rewrite absolute paths (/_next/, /assets/) to relative so <base> tag resolves them
      html = html.replace(/(href|src|content)="\/(_next|assets)\//g, '$1="$2/');

      // Force-hide preloaders that block content in srcDoc iframes
      html = html.replace(/<\/head>/i, `<style>.preloader{display:none!important;opacity:0!important}</style></head>`);

      html = html.replace(/BRAND_PRIMARY/g, brandKit.colors.primary);
      html = html.replace(/BRAND_SECONDARY/g, brandKit.colors.secondary);
      html = html.replace(/BRAND_ACCENT/g, brandKit.colors.accent);
      html = html.replace(/BRAND_BG/g, brandKit.colors.background);
      html = html.replace(/BRAND_TEXT/g, brandKit.colors.text);
      html = html.replace(/FONT_HEADING/g, brandKit.fonts.heading);
      html = html.replace(/FONT_BODY/g, brandKit.fonts.body);
      const h = brandKit.fonts.heading.replace(/ /g, '+');
      const b = brandKit.fonts.body.replace(/ /g, '+');
      const fontsUrl = h === b
        ? `https://fonts.googleapis.com/css2?family=${h}:wght@300;400;500;600;700&display=swap`
        : `https://fonts.googleapis.com/css2?family=${h}:wght@300;400;500;600;700&family=${b}:wght@300;400;500;600;700&display=swap`;
      html = html.replace(/GOOGLE_FONTS_URL/g, fontsUrl);

      // Envato templates use hardcoded CSS — inject !important overrides for brand preview
      const isEnvatoTemplate = !html.includes('var(--heading-font)') && !html.includes('var(--body-font)');
      if (isEnvatoTemplate) {
        const brandOverride = `
        <link rel="preconnect" href="https://fonts.googleapis.com">
        <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
        <link href="${fontsUrl}" rel="stylesheet">
        <style id="orbis-brand-override">
          h1,h2,h3,h4,h5,h6,[class*="title"],[class*="heading"],[class*="headline"] {
            font-family: '${brandKit.fonts.heading}', sans-serif !important;
          }
          body,p,li,span,a,input,textarea,button,[class*="description"],[class*="text"] {
            font-family: '${brandKit.fonts.body}', sans-serif !important;
          }
          body { background-color: ${brandKit.colors.background} !important; color: ${brandKit.colors.text} !important; }
          .btn-primary,.btn--primary,.theme-btn,[class*="btn-primary"],a.btn {
            background-color: ${brandKit.colors.primary} !important; border-color: ${brandKit.colors.primary} !important;
          }
          a:hover,.section-title span,.section-tag {
            color: ${brandKit.colors.primary} !important;
          }
          [class*="highlight"],[class*="accent"],.badge,.tag,
          .btn-accent,.btn--accent,[class*="btn-accent"] {
            background-color: ${brandKit.colors.accent} !important; border-color: ${brandKit.colors.accent} !important;
          }
          .btn-secondary,.btn--secondary,[class*="btn-secondary"],[class*="btn-outline"] {
            border-color: ${brandKit.colors.secondary} !important; color: ${brandKit.colors.secondary} !important;
          }
        </style>`;
        html = html.replace(/<\/head>/i, `${brandOverride}\n</head>`);

        // Direct hex replacement: swap template's default colors for user's brand colors
        const defaults = template.defaultBrandKit?.colors || {};
        const swaps = [
          [defaults.primary, brandKit.colors.primary],
          [defaults.secondary, brandKit.colors.secondary],
          [defaults.accent, brandKit.colors.accent],
          [defaults.background, brandKit.colors.background],
          [defaults.text, brandKit.colors.text],
        ];
        for (const [from, to] of swaps) {
          if (from && to && from.toLowerCase() !== to.toLowerCase()) {
            html = html.replaceAll(from, to);
            html = html.replaceAll(from.toLowerCase(), to);
            html = html.replaceAll(from.toUpperCase(), to);
          }
        }
      }

      // Logo replacement in preview
      if (brandKit.logoUrl) {
        html = html.replace(
          /(<a[^>]*class="[^"]*(?:navbar-brand|logo)[^"]*"[^>]*>\s*<img[^>]*\s)src="[^"]*"/gi,
          `$1src="${brandKit.logoUrl}"`
        );
        html = html.replace(
          /(<img[^>]*(?:class="[^"]*logo[^"]*"|alt="[^"]*logo[^"]*")[^>]*\s)src="[^"]*"/gi,
          `$1src="${brandKit.logoUrl}"`
        );
      }

      const mergedContent = {};
      if (page.sections) {
        page.sections.forEach(section => {
          if (section.contentSlots) {
            section.contentSlots.forEach(slot => {
              mergedContent[slot.key] = slot.defaultValue || '';
            });
          }
        });
      }
      Object.entries({ ...mergedContent, ...contentOverrides }).forEach(([key, value]) => {
        const legacyRegex = new RegExp(`ORBIS_SLOT:${key}:\\w+`, 'g');
        html = html.replace(legacyRegex, value);
        const envatoRegex = new RegExp(`(<!--\\s*ORBIS_SLOT:${key}\\s*-->)(.*?)(<!--\\s*/ORBIS_SLOT\\s*-->)`, 'gs');
        html = html.replace(envatoRegex, `$1${value}$3`);
      });

      const mergedImages = {};
      if (page.sections) {
        page.sections.forEach(section => {
          if (section.imageSlots) {
            section.imageSlots.forEach(slot => {
              if (slot.defaultImage) mergedImages[slot.key] = slot.defaultImage;
            });
          }
        });
      }
      Object.entries({ ...mergedImages, ...imageOverrides }).forEach(([key, url]) => {
        html = html.replace(new RegExp(`ORBIS_IMAGE:${key}`, 'g'), url);
      });

      setPreviewHtml(html);
    } catch (e) {
      console.error('Preview build failed:', e);
    }
  }, [template, currentPageIdx, brandKit, contentOverrides, imageOverrides, magicState]);

  useEffect(() => { buildPreview(); }, [buildPreview]);

  // ── Save handlers ──
  const saveBrand = async () => {
    if (!projectId) return;
    setSaving(true);
    try {
      await axios.post(`${API_URL}/templates/projects/${projectId}/brand`, {
        brand_context: {
          brand_path: 'full',
          logo_url: brandKit.logoUrl,
          colors: brandKit.colors,
          heading_font: brandKit.fonts.heading,
          body_font: brandKit.fonts.body,
        },
      });
      toast.success('Brand saved');
    } catch (e) {
      toast.success('Brand updated (local)');
    } finally {
      setSaving(false);
    }
  };

  const saveContent = async () => {
    if (!projectId) return;
    setSaving(true);
    try {
      await axios.post(`${API_URL}/templates/projects/${projectId}/content`, {
        content_overrides: contentOverrides,
      });
      await axios.post(`${API_URL}/templates/projects/${projectId}/images`, {
        image_overrides: imageOverrides,
      });
      toast.success('Content saved');
    } catch (e) {
      toast.success('Content updated (local)');
    } finally {
      setSaving(false);
    }
  };

  // ── Load vault pages for the Pages step ──
  const loadVaultPages = async (slug) => {
    try {
      const resp = await axios.get(`${API_URL}/templates/vault/${slug}/pages`);
      setVaultPages(resp.data);
      // Auto-select all "pages" category, skip detail/utility by default
      const autoSelected = new Set();
      (resp.data.pages || []).forEach(p => {
        if (p.category === 'pages' || p.is_home) autoSelected.add(p.filename);
      });
      // If very few pages (≤6), select all
      if (resp.data.total_pages <= 6) {
        resp.data.pages.forEach(p => autoSelected.add(p.filename));
      }
      setSelectedPages(autoSelected);
    } catch (e) {
      console.warn('Could not load vault pages:', e);
    }
  };

  // ── Render: one-time, then ReviewStage takes over ──
  const handleRender = async () => {
    if (!projectId) { toast.error('No project'); return; }
    setRendering(true);
    try {
      await saveBrand();
      await saveContent();
      const resp = await axios.post(`${API_URL}/templates/projects/${projectId}/render`, {
        selected_pages: selectedPages.size > 0 ? [...selectedPages] : undefined,
      });
      const state = resp.data.template_state;
      setMagicState(state);
      toast.success(`Rendered ${resp.data.pages_count} pages — visual editor ready`);
    } catch (e) {
      // Handle "already rendered" guard gracefully
      if (e.response?.status === 400 && e.response?.data?.detail?.includes('Already rendered')) {
        setRendering(false);
        const confirmed = window.confirm(
          'Your site has already been rendered.\n\nClick OK to reset and re-render from scratch — your current AI edits will be saved as a version checkpoint.'
        );
        if (confirmed) {
          await handleReset();
        }
        return;
      }
      toast.error(e.response?.data?.detail || 'Render failed');
    } finally {
      setRendering(false);
    }
  };

  // ── Reset to template (re-render from vault) ──
  const handleReset = async () => {
    if (!projectId) return;
    try {
      const resp = await axios.post(`${API_URL}/templates/projects/${projectId}/reset`);
      const state = resp.data.template_state;
      setMagicState(state);
      toast.success('Reset to template — previous version saved');
    } catch (e) {
      toast.error('Reset failed');
    }
  };

  // ── ReviewStage callbacks ──
  const handleEdit = (newMagicState) => {
    setMagicState(newMagicState);
  };

  const handleDownload = async () => {
    if (!projectId) return;
    try {
      const resp = await axios.get(`${API_URL}/templates/projects/${projectId}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `${template?.name || 'template'}_website.zip`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Download failed');
    }
  };

  const handleApprove = async () => {
    // For templates, "approve" means go to deploy
    navigate(`/dashboard`);
    toast.success('Site ready to publish from your dashboard');
  };

  // ── AI Rewrite ──
  const handleRewrite = async (slotId, currentValue) => {
    if (!projectId) { toast.error('Save project first'); return; }
    try {
      const resp = await axios.post(`${API_URL}/templates/projects/${projectId}/rewrite`, {
        slot_id: slotId,
        current_value: currentValue,
        style: 'professional',
      });
      setContentOverrides(prev => ({ ...prev, [slotId]: resp.data.rewritten }));
      toast.success('Rewritten with AI');
    } catch (e) {
      toast.error('AI rewrite failed');
    }
  };

  const handleContinue = async () => {
    if (step === 0) {
      await saveBrand();
      // Load vault pages when entering Pages step
      if (!vaultPages && template) {
        await loadVaultPages(template.slug);
      }
      setStep(1);
    }
    else if (step === 1) { setStep(2); }
    else if (step === 2) { await saveContent(); await handleRender(); }
  };

  // ── Loading ──
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--orbis-bg)' }}>
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: 'var(--orbis-t3)' }} />
      </div>
    );
  }

  if (!template) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--orbis-bg)' }}>
        <p style={{ color: 'var(--orbis-t3)' }}>Template not found</p>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════
  // POST-RENDER: Full visual editor via ReviewStage
  // ═══════════════════════════════════════════════════════════════════
  if (magicState) {
    return (
      <ReviewStage
        code={magicState.generated_code}
        designSystem={magicState.design_system || {
          colors: brandKit.colors,
          typography: { heading: brandKit.fonts.heading, body: brandKit.fonts.body },
        }}
        generationMode={magicState.generation_mode}
        generatedPages={magicState.generated_pages}
        projectId={projectId}
        onApprove={handleApprove}
        onDownload={handleDownload}
        onEdit={handleEdit}
        isProcessing={saving || rendering}
        mode="template"
        modeContext={{
          template: {
            id: template?.id,
            slug: template?.slug,
            name: template?.name,
          },
        }}
        reviewCapabilities={{
          supportsReferenceContext: false,
        }}
      />
    );
  }

  // ═══════════════════════════════════════════════════════════════════
  // PRE-RENDER: Brand → Content wizard with live preview
  // ═══════════════════════════════════════════════════════════════════
  const viewportWidth = viewport === 'mobile' ? 375 : viewport === 'tablet' ? 768 : '100%';

  return (
    <div className="min-h-screen flex flex-col" style={{ background: 'var(--orbis-bg)' }}>
      {/* ── Nav ── */}
      <nav className="relative z-10 flex items-center justify-center shrink-0" style={{ height: '56px', background: 'var(--surface-0)', borderBottom: '1px solid var(--orbis-border)' }}>
        <div className="absolute top-1/2 -translate-y-1/2 flex items-center gap-3" style={{ left: 20 }}>
          <button onClick={() => navigate('/dashboard')} className="flex items-center gap-1.5 hover:opacity-80 transition-opacity group" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--orbis-t3)' }}>
            <ArrowLeft className="w-3.5 h-3.5 group-hover:-translate-x-0.5 transition-transform" />
            <OrbisMarkSVG size={18} />
            <span style={{ fontFamily: 'var(--orbis-font)', fontSize: '14px', color: 'var(--orbis-t2)' }}>Projects</span>
          </button>
          <span style={{ fontFamily: 'var(--orbis-font)', fontSize: '13px', color: 'var(--orbis-t2)' }}>{template.name}</span>
        </div>

        {/* Step pills */}
        <div className="flex items-center gap-1">
          {PRE_RENDER_STEPS.map((s, i) => (
            <button
              key={s}
              onClick={() => { if (i <= step) setStep(i); }}
              className="flex items-center gap-1.5"
              style={{
                padding: '5px 14px', borderRadius: '100px', fontSize: '12px', fontWeight: i === step ? 500 : 400,
                fontFamily: 'var(--orbis-font)', border: 'none', cursor: i <= step ? 'pointer' : 'default', transition: 'all 0.15s',
                background: i < step ? 'var(--orbis-green)' : i === step ? 'rgba(250,250,250,0.06)' : 'transparent',
                color: i < step ? '#000' : i === step ? 'var(--orbis-t1)' : 'var(--orbis-t3)',
              }}
            >
              {i < step ? <Check className="w-3 h-3" /> : <span style={{ width: 16, height: 16, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', background: i === step ? 'rgba(250,250,250,0.08)' : 'transparent' }}>{i + 1}</span>}
              {s}
            </button>
          ))}
          {/* Render pill — always last */}
          <button
            onClick={handleRender}
            disabled={rendering || step < 2}
            className="flex items-center gap-1.5"
            style={{
              padding: '5px 14px', borderRadius: '100px', fontSize: '12px', fontWeight: 500,
              fontFamily: 'var(--orbis-font)', border: 'none', transition: 'all 0.15s',
              cursor: step >= 2 && !rendering ? 'pointer' : 'default',
              background: 'var(--orbis-blue)', color: '#1A1A1A',
              opacity: step >= 2 ? 1 : 0.4,
            }}
          >
            {rendering ? <Loader2 className="w-3 h-3 animate-spin" /> : <Eye className="w-3 h-3" />}
            Render & Edit
          </button>
        </div>

        {/* Viewport toggle */}
        <div className="absolute top-1/2 -translate-y-1/2 flex items-center gap-1" style={{ right: 20 }}>
          {[
            { id: 'desktop', Icon: Monitor },
            { id: 'tablet', Icon: Tablet },
            { id: 'mobile', Icon: Smartphone },
          ].map(({ id, Icon }) => (
            <button
              key={id}
              onClick={() => setViewport(id)}
              style={{
                padding: '6px 8px', borderRadius: '6px', border: 'none', cursor: 'pointer',
                background: viewport === id ? 'rgba(250,250,250,0.06)' : 'transparent',
                color: viewport === id ? 'var(--orbis-t1)' : 'var(--orbis-t3)',
              }}
            ><Icon className="w-4 h-4" /></button>
          ))}
        </div>
      </nav>

      {/* ── Main content ── */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left panel */}
        <div className="shrink-0 overflow-y-auto" style={{ width: 380, borderRight: '1px solid var(--orbis-border)', background: 'var(--orbis-s1)' }}>
          <AnimatePresence mode="wait">
            {step === 0 && (
              <BrandPanel key="brand" brandKit={brandKit} setBrandKit={setBrandKit} template={template} projectId={projectId} />
            )}
            {step === 1 && (
              <PagesStep key="pages" vaultPages={vaultPages} selectedPages={selectedPages} setSelectedPages={setSelectedPages} template={template} loading={!vaultPages} />
            )}
            {step === 2 && (
              <ContentPanel key="content" template={template} contentOverrides={contentOverrides} setContentOverrides={setContentOverrides} imageOverrides={imageOverrides} setImageOverrides={setImageOverrides} onRewrite={handleRewrite} currentPageIdx={currentPageIdx} setCurrentPageIdx={setCurrentPageIdx} />
            )}
          </AnimatePresence>
        </div>

        {/* Right: Preview */}
        <div className="flex-1 flex items-center justify-center p-4" style={{ background: '#111' }}>
          <div style={{ width: viewportWidth, maxWidth: '100%', height: '100%', borderRadius: '8px', overflow: 'hidden', border: '1px solid var(--orbis-border)', transition: 'width 0.3s' }}>
            {previewHtml ? (
              <iframe
                srcDoc={previewHtml}
                className="w-full h-full"
                style={{ border: 'none' }}
                sandbox="allow-scripts allow-same-origin"
                title="Template Preview"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center" style={{ background: 'var(--orbis-s2)' }}>
                <Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--orbis-t3)' }} />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Bottom Action Bar ── */}
      <div className="magic-bottom-bar shrink-0 flex items-center justify-between" style={{ height: 56, padding: '0 24px', background: 'var(--orbis-s1)', borderTop: '1px solid var(--orbis-border)' }}>
        <button
          onClick={() => { if (step > 0) setStep(step - 1); else navigate('/dashboard'); }}
          className="flex items-center gap-2"
          style={{ padding: '8px 16px', borderRadius: '100px', fontSize: '13px', background: 'transparent', border: '1px solid var(--orbis-border)', color: 'var(--orbis-t2)', cursor: 'pointer' }}
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Back
        </button>

        <div className="flex items-center gap-1.5">
          {PRE_RENDER_STEPS.map((_, i) => (
            <div key={i} style={{ width: 6, height: 6, borderRadius: '50%', background: i <= step ? 'var(--orbis-t1)' : 'var(--orbis-s3)' }} />
          ))}
        </div>

        <button
          onClick={handleContinue}
          disabled={saving || rendering}
          className="flex items-center gap-2"
          style={{ padding: '8px 20px', borderRadius: '100px', fontSize: '13px', fontWeight: 500, background: step === 1 ? 'var(--orbis-blue)' : 'var(--orbis-blue)', color: '#1A1A1A', border: 'none', cursor: 'pointer' }}
        >
          {saving || rendering ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
          {step === 2 ? <>Render & Edit <Eye className="w-3.5 h-3.5" /></> : <>Continue <ArrowRight className="w-3.5 h-3.5" /></>}
        </button>
      </div>
    </div>
  );
}

// ─── Palette presets ─────────────────────────────────────────────────────────
const PALETTE_PRESETS = [
  { name: 'Purple Tech', colors: { primary: '#7c3aed', secondary: '#06b6d4', accent: '#f59e0b', background: '#09090b', text: '#fafafa' } },
  { name: 'Ocean', colors: { primary: '#0055DC', secondary: '#C5EBF5', accent: '#6ECCE5', background: '#ffffff', text: '#36393a' } },
  { name: 'Forest', colors: { primary: '#a9e448', secondary: '#bccbb7', accent: '#6dcf6d', background: '#F7F7F2', text: '#000000' } },
  { name: 'Ember', colors: { primary: '#fe5b2c', secondary: '#282828', accent: '#f59e0b', background: '#f6f6f1', text: '#1b1a1a' } },
  { name: 'Midnight', colors: { primary: '#8b5cf6', secondary: '#374151', accent: '#10b981', background: '#030712', text: '#f9fafb' } },
];

// ═══ Brand Panel ═══
function BrandPanel({ brandKit, setBrandKit, template, projectId }) {
  const fileInputRef = useRef(null);
  const [aiBrandLoading, setAiBrandLoading] = useState(false);

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!projectId) { toast.error('Save project first'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('Max 5MB'); return; }
    const formData = new FormData();
    formData.append('file', file);
    try {
      const resp = await axios.post(
        `${API_URL}/magic/projects/${projectId}/logo`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      setBrandKit(prev => ({ ...prev, logoUrl: resp.data.logo_url }));
      toast.success('Logo uploaded');
    } catch (err) {
      toast.error('Failed to upload logo');
    }
  };

  const handleAiBrandFill = async () => {
    const domain = window.prompt('Enter your website URL (e.g. apple.com or https://yoursite.com):');
    if (!domain) return;
    setAiBrandLoading(true);
    try {
      const resp = await axios.post(`${API_URL}/brand/extract`, { url: domain });
      if (resp.data.colors) setBrandKit(prev => ({ ...prev, colors: { ...prev.colors, ...resp.data.colors } }));
      if (resp.data.fonts) setBrandKit(prev => ({ ...prev, fonts: { ...prev.fonts, ...resp.data.fonts } }));
      toast.success('Brand colors extracted from your website!');
    } catch (e) {
      toast.error('Could not extract brand — try setting colors manually');
    } finally {
      setAiBrandLoading(false);
    }
  };

  return (
    <motion.div initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -10 }} style={{ padding: '24px' }}>
      <h2 style={{ fontSize: '18px', fontWeight: 600, color: 'var(--orbis-t1)', marginBottom: '4px' }}>Brand Your Site</h2>
      <p style={{ fontSize: '13px', color: 'var(--orbis-t3)', marginBottom: '20px' }}>Customize colors, fonts, and logo</p>

      {/* AI brand auto-fill */}
      <button
        onClick={handleAiBrandFill}
        disabled={aiBrandLoading}
        style={{ width: '100%', padding: '10px 14px', marginBottom: '20px', borderRadius: '8px', border: '1px dashed var(--orbis-border)', background: 'transparent', color: aiBrandLoading ? 'var(--orbis-t4)' : 'var(--orbis-t3)', fontSize: '12px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, transition: 'all 0.15s' }}
      >
        {aiBrandLoading ? <Loader2 size={14} style={{ animation: 'spin 0.8s linear infinite' }} /> : <Globe size={14} />}
        {aiBrandLoading ? 'Extracting brand…' : 'Auto-fill brand from my website'}
      </button>

      {/* Colors */}
      <div style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
          <label style={{ fontSize: '11px', fontFamily: 'var(--orbis-mono)', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--orbis-t3)' }}>Colors</label>
          <button
            onClick={() => setBrandKit(prev => ({ ...prev, colors: template.defaultBrandKit.colors }))}
            style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '10px', color: 'var(--orbis-t4)', background: 'none', border: 'none', cursor: 'pointer' }}
          >
            <RotateCcw size={10} /> Reset
          </button>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {Object.entries(brandKit.colors).map(([key, value]) => (
            <div key={key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontSize: '12px', color: 'var(--orbis-t2)', textTransform: 'capitalize', minWidth: 80 }}>{key}</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input
                  type="text"
                  value={value}
                  onChange={e => {
                    const hex = e.target.value;
                    if (/^#[0-9A-Fa-f]{0,6}$/.test(hex)) {
                      setBrandKit(prev => ({ ...prev, colors: { ...prev.colors, [key]: hex } }));
                    }
                  }}
                  style={{ width: 80, padding: '4px 8px', borderRadius: '6px', border: '1px solid var(--orbis-border)', background: 'var(--orbis-s2)', color: 'var(--orbis-t2)', fontFamily: 'var(--orbis-mono)', fontSize: '11px', outline: 'none' }}
                />
                <input
                  type="color"
                  value={value.length === 7 ? value : '#000000'}
                  onChange={e => setBrandKit(prev => ({ ...prev, colors: { ...prev.colors, [key]: e.target.value } }))}
                  style={{ width: 28, height: 28, borderRadius: '6px', border: '1px solid var(--orbis-border)', cursor: 'pointer', background: 'none', padding: 0 }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Palette Presets */}
      <div style={{ marginBottom: '20px' }}>
        <label style={{ fontSize: '11px', fontFamily: 'var(--orbis-mono)', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--orbis-t3)', display: 'block', marginBottom: '10px' }}>Palette Presets</label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {PALETTE_PRESETS.map(preset => (
            <button
              key={preset.name}
              onClick={() => setBrandKit(prev => ({ ...prev, colors: preset.colors }))}
              title={preset.name}
              style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '4px 10px', borderRadius: '20px', background: 'var(--orbis-s2)', border: '1px solid var(--orbis-border)', cursor: 'pointer', transition: 'all 0.15s' }}
            >
              <div style={{ display: 'flex', gap: 2 }}>
                {Object.values(preset.colors).slice(0, 3).map((c, i) => (
                  <div key={i} style={{ width: 10, height: 10, borderRadius: '50%', background: c, border: '1px solid rgba(255,255,255,0.1)' }} />
                ))}
              </div>
              <span style={{ fontSize: '11px', color: 'var(--orbis-t3)' }}>{preset.name}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Fonts */}
      <div style={{ marginBottom: '20px' }}>
        <label style={{ fontSize: '11px', fontFamily: 'var(--orbis-mono)', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--orbis-t3)', display: 'block', marginBottom: '12px' }}>Typography</label>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <span style={{ fontSize: '12px', color: 'var(--orbis-t3)', display: 'block', marginBottom: '4px' }}>Heading Font</span>
            <select
              value={brandKit.fonts.heading}
              onChange={e => setBrandKit(prev => ({ ...prev, fonts: { ...prev.fonts, heading: e.target.value } }))}
              style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', fontSize: '13px', background: 'var(--orbis-s2)', border: '1px solid var(--orbis-border)', color: 'var(--orbis-t1)', fontFamily: brandKit.fonts.heading, outline: 'none' }}
            >
              {GOOGLE_FONTS.map(f => <option key={f} value={f} style={{ fontFamily: f }}>{f}</option>)}
            </select>
          </div>
          <div>
            <span style={{ fontSize: '12px', color: 'var(--orbis-t3)', display: 'block', marginBottom: '4px' }}>Body Font</span>
            <select
              value={brandKit.fonts.body}
              onChange={e => setBrandKit(prev => ({ ...prev, fonts: { ...prev.fonts, body: e.target.value } }))}
              style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', fontSize: '13px', background: 'var(--orbis-s2)', border: '1px solid var(--orbis-border)', color: 'var(--orbis-t1)', fontFamily: brandKit.fonts.body, outline: 'none' }}
            >
              {GOOGLE_FONTS.map(f => <option key={f} value={f} style={{ fontFamily: f }}>{f}</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* Logo */}
      <div style={{ marginBottom: '20px' }}>
        <label style={{ fontSize: '11px', fontFamily: 'var(--orbis-mono)', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--orbis-t3)', display: 'block', marginBottom: '12px' }}>Logo</label>
        <input
          type="file"
          accept="image/png,image/jpeg,image/svg+xml,image/webp,image/gif"
          style={{ display: 'none' }}
          ref={fileInputRef}
          onChange={handleLogoUpload}
        />
        <div
          onClick={() => fileInputRef.current?.click()}
          style={{ height: 80, borderRadius: '8px', border: '1px dashed var(--orbis-border)', background: 'var(--orbis-s2)', color: 'var(--orbis-t4)', fontSize: '12px', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
        >
          {brandKit.logoUrl ? (
            <img src={brandKit.logoUrl} alt="Logo" style={{ maxHeight: '100%', maxWidth: '100%', objectFit: 'contain' }} />
          ) : (
            <><Upload size={14} style={{ marginRight: 6 }} /> Click to upload logo</>
          )}
        </div>
        {brandKit.logoUrl && (
          <button onClick={() => setBrandKit(prev => ({ ...prev, logoUrl: null }))} style={{ marginTop: 6, fontSize: '11px', color: 'var(--orbis-t4)', background: 'none', border: 'none', cursor: 'pointer' }}>Remove logo</button>
        )}
      </div>

      {/* Live color preview strip */}
      <div style={{ padding: '14px', borderRadius: '10px', background: 'var(--orbis-s2)', border: '1px solid var(--orbis-border)' }}>
        <p style={{ fontSize: '10px', fontFamily: 'var(--orbis-mono)', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--orbis-t4)', marginBottom: '10px' }}>Color Preview</p>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          {Object.entries(brandKit.colors).map(([key, c]) => (
            <div key={key} title={key} style={{ flex: 1, height: 32, background: c, borderRadius: '6px', border: '1px solid rgba(255,255,255,0.07)' }} />
          ))}
        </div>
        <p style={{ fontSize: '11px', marginTop: 10, color: 'var(--orbis-t4)' }}>
          <span style={{ fontFamily: brandKit.fonts.heading, marginRight: 8, color: brandKit.colors.primary }}>{brandKit.fonts.heading}</span>
          <span style={{ fontFamily: brandKit.fonts.body, color: brandKit.colors.text || 'var(--orbis-t3)' }}>{brandKit.fonts.body}</span>
        </p>
      </div>
    </motion.div>
  );
}

// ═══ Pages Step ═══
const CATEGORY_LABELS = { pages: 'Pages', detail: 'Detail Pages', utility: 'Utility Pages' };

function PagesStep({ vaultPages, selectedPages, setSelectedPages, template, loading }) {
  if (loading) {
    return (
      <motion.div initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -10 }} style={{ padding: '24px' }}>
        <h2 style={{ fontSize: '18px', fontWeight: 600, color: 'var(--orbis-t1)', marginBottom: '4px' }}>Choose Pages</h2>
        <p style={{ fontSize: '13px', color: 'var(--orbis-t3)', marginBottom: '24px' }}>Loading available pages...</p>
        <div className="flex items-center justify-center" style={{ padding: '40px' }}>
          <Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--orbis-t3)' }} />
        </div>
      </motion.div>
    );
  }

  const grouped = vaultPages?.grouped || {};
  const totalSelected = selectedPages.size;
  const totalAvailable = vaultPages?.total_pages || 0;

  const toggle = (filename) => {
    setSelectedPages(prev => {
      const next = new Set(prev);
      if (next.has(filename)) next.delete(filename);
      else next.add(filename);
      return next;
    });
  };

  const selectAll = () => {
    const all = new Set();
    (vaultPages?.pages || []).forEach(p => all.add(p.filename));
    setSelectedPages(all);
  };

  const selectNone = () => {
    // Keep home page always selected
    const home = new Set();
    (vaultPages?.pages || []).forEach(p => { if (p.is_home) home.add(p.filename); });
    setSelectedPages(home);
  };

  return (
    <motion.div initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -10 }} style={{ padding: '24px' }}>
      <h2 style={{ fontSize: '18px', fontWeight: 600, color: 'var(--orbis-t1)', marginBottom: '4px' }}>Choose Pages</h2>
      <p style={{ fontSize: '13px', color: 'var(--orbis-t3)', marginBottom: '16px' }}>
        Select which pages to include in your site ({totalSelected} of {totalAvailable})
      </p>

      {/* Quick actions */}
      <div className="flex gap-2 mb-5">
        <button onClick={selectAll} style={{ padding: '4px 12px', borderRadius: '6px', fontSize: '11px', background: 'var(--orbis-s2)', border: '1px solid var(--orbis-border)', color: 'var(--orbis-t2)', cursor: 'pointer' }}>
          Select All
        </button>
        <button onClick={selectNone} style={{ padding: '4px 12px', borderRadius: '6px', fontSize: '11px', background: 'var(--orbis-s2)', border: '1px solid var(--orbis-border)', color: 'var(--orbis-t2)', cursor: 'pointer' }}>
          Home Only
        </button>
      </div>

      {/* Page groups */}
      {['pages', 'detail', 'utility'].map(cat => {
        const items = grouped[cat];
        if (!items || items.length === 0) return null;
        return (
          <div key={cat} style={{ marginBottom: '20px' }}>
            <div className="flex items-center gap-2 mb-3">
              <Layers className="w-3.5 h-3.5" style={{ color: 'var(--orbis-t3)' }} />
              <span style={{ fontSize: '11px', fontFamily: 'var(--orbis-mono)', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--orbis-t3)' }}>
                {CATEGORY_LABELS[cat]} ({items.filter(p => selectedPages.has(p.filename)).length}/{items.length})
              </span>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {items.map(page => {
                const isSelected = selectedPages.has(page.filename);
                return (
                  <button
                    key={page.filename}
                    onClick={() => { if (!page.is_home) toggle(page.filename); }}
                    className="flex items-center gap-2.5 text-left"
                    style={{
                      padding: '10px 12px',
                      borderRadius: 'var(--orbis-r-md)',
                      border: `1px solid ${isSelected ? 'var(--orbis-blue)' : 'var(--orbis-border)'}`,
                      background: isSelected ? 'rgba(184,204,223,0.06)' : 'var(--orbis-s2)',
                      cursor: page.is_home ? 'default' : 'pointer',
                      transition: 'all 0.15s',
                      opacity: page.is_home ? 0.7 : 1,
                    }}
                  >
                    {isSelected
                      ? <CheckSquare className="w-4 h-4 shrink-0" style={{ color: 'var(--orbis-blue)' }} />
                      : <Square className="w-4 h-4 shrink-0" style={{ color: 'var(--orbis-t4)' }} />
                    }
                    <div className="min-w-0">
                      <div style={{ fontSize: '13px', color: isSelected ? 'var(--orbis-t1)' : 'var(--orbis-t3)', fontWeight: isSelected ? 500 : 400, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {page.name}
                      </div>
                      {page.is_home && (
                        <div style={{ fontSize: '10px', color: 'var(--orbis-t4)', fontFamily: 'var(--orbis-mono)' }}>always included</div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}

      {totalSelected === 0 && (
        <div style={{ padding: '16px', borderRadius: '8px', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', fontSize: '12px', color: '#ef4444' }}>
          Select at least one page to continue.
        </div>
      )}
    </motion.div>
  );
}

// ═══ Content Panel ═══
function ContentPanel({ template, contentOverrides, setContentOverrides, imageOverrides, setImageOverrides, onRewrite, currentPageIdx, setCurrentPageIdx }) {
  const [expandedSections, setExpandedSections] = useState({});
  const toggleSection = (id) => setExpandedSections(prev => ({ ...prev, [id]: !prev[id] }));
  const currentPage = template.pages[currentPageIdx];

  return (
    <motion.div initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -10 }} style={{ padding: '24px' }}>
      <h2 style={{ fontSize: '18px', fontWeight: 600, color: 'var(--orbis-t1)', marginBottom: '4px' }}>Edit Content</h2>
      <p style={{ fontSize: '13px', color: 'var(--orbis-t3)', marginBottom: '20px' }}>Customize text and images</p>

      {/* Page tabs */}
      <div className="flex gap-1 mb-6 overflow-x-auto" style={{ scrollbarWidth: 'none' }}>
        {template.pages.map((p, i) => (
          <button
            key={p.id}
            onClick={() => setCurrentPageIdx(i)}
            style={{
              padding: '5px 12px', borderRadius: '8px', fontSize: '12px', whiteSpace: 'nowrap',
              fontFamily: 'var(--orbis-font)', border: 'none', cursor: 'pointer',
              background: currentPageIdx === i ? 'rgba(250,250,250,0.06)' : 'transparent',
              color: currentPageIdx === i ? 'var(--orbis-t1)' : 'var(--orbis-t3)',
            }}
          >{p.name}</button>
        ))}
      </div>

      {/* Sections */}
      {currentPage.sections.length === 0 ? (
        <div style={{ padding: '20px', textAlign: 'center', color: 'var(--orbis-t4)', fontSize: '13px' }}>
          This page uses the template's default content. Edit by previewing the site.
        </div>
      ) : (
        currentPage.sections.map(section => (
          <div key={section.id} style={{ marginBottom: '12px', borderRadius: '10px', border: '1px solid var(--orbis-border)', overflow: 'hidden' }}>
            <button
              onClick={() => toggleSection(section.id)}
              className="w-full flex items-center justify-between"
              style={{ padding: '12px 16px', background: 'var(--orbis-s2)', border: 'none', cursor: 'pointer', color: 'var(--orbis-t2)', fontSize: '13px', fontWeight: 500 }}
            >
              {section.label}
              {expandedSections[section.id] ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            </button>
            {expandedSections[section.id] && (
              <div style={{ padding: '12px 16px', background: 'var(--orbis-bg)' }}>
                {section.contentSlots.map(slot => (
                  <div key={slot.id} style={{ marginBottom: '12px' }}>
                    <div className="flex items-center justify-between mb-1">
                      <label style={{ fontSize: '11px', color: 'var(--orbis-t4)', fontFamily: 'var(--orbis-mono)' }}>{slot.key}</label>
                      {slot.aiEnhanceable && (
                        <button
                          onClick={() => onRewrite(slot.key, contentOverrides[slot.key] || slot.defaultValue)}
                          className="flex items-center gap-1"
                          style={{ fontSize: '10px', color: 'var(--orbis-blue)', background: 'none', border: 'none', cursor: 'pointer' }}
                        >
                          <Sparkles className="w-3 h-3" /> Rewrite
                        </button>
                      )}
                    </div>
                    {slot.type === 'body' ? (
                      <textarea
                        value={contentOverrides[slot.key] ?? slot.defaultValue}
                        onChange={e => setContentOverrides(prev => ({ ...prev, [slot.key]: e.target.value }))}
                        rows={3}
                        style={{ width: '100%', padding: '8px', borderRadius: '6px', fontSize: '12px', background: 'var(--orbis-s2)', border: '1px solid var(--orbis-border)', color: 'var(--orbis-t1)', resize: 'vertical', fontFamily: 'var(--orbis-font)' }}
                      />
                    ) : (
                      <input
                        type="text"
                        value={contentOverrides[slot.key] ?? slot.defaultValue}
                        onChange={e => setContentOverrides(prev => ({ ...prev, [slot.key]: e.target.value }))}
                        maxLength={slot.maxLength}
                        style={{ width: '100%', padding: '8px', borderRadius: '6px', fontSize: '12px', background: 'var(--orbis-s2)', border: '1px solid var(--orbis-border)', color: 'var(--orbis-t1)', fontFamily: 'var(--orbis-font)' }}
                      />
                    )}
                  </div>
                ))}
                {section.imageSlots.map(slot => (
                  <div key={slot.id} style={{ marginBottom: '12px' }}>
                    <label style={{ fontSize: '11px', color: 'var(--orbis-t4)', fontFamily: 'var(--orbis-mono)', display: 'block', marginBottom: '4px' }}>{slot.label}</label>
                    <input
                      type="text"
                      value={imageOverrides[slot.key] || ''}
                      onChange={e => setImageOverrides(prev => ({ ...prev, [slot.key]: e.target.value }))}
                      placeholder={slot.defaultImage ? 'Using default image' : 'Paste image URL'}
                      style={{ width: '100%', padding: '8px', borderRadius: '6px', fontSize: '12px', background: 'var(--orbis-s2)', border: '1px solid var(--orbis-border)', color: 'var(--orbis-t1)', fontFamily: 'var(--orbis-font)' }}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        ))
      )}
    </motion.div>
  );
}
